import React, { useEffect, useState, useRef, memo } from "react";
import { useNavigate } from 'react-router-dom';
import NewWorldDbStatusFilter from './NewWorldDbStatusFilter';
import WorldDbImportFilter from './WorldDbImportFilter';
import WorldDbFlowFilter from './WorldDbFlowFilter';
import NewWorldDbSearch from './NewWorldDbSearch';
import DuplicateAlertsBadge from './DuplicateAlertsBadge';
import WorldDbDownloadModal from './WorldDbDownloadModal';
import "./WorldDbTableNav.css";

const MobileModal = memo(({ mobileModalMode, handleJustCloseModal, handleMobileSelect, customAmount, setCustomAmount, customInputRef, displayedCount, handleCustomSelect, selectedCount, handleActionAndClose, hasWarmupFlows, handleBulkStartFlow, handleOpenAiCleaner, handleStartMassSpy, handleOpenAiManager, showAutoSendButton, handleOpenAutoSend, hasOutreachJobs, handleOpenOutreachJobModal }) => (
  <div className="worlddb-mobile-modal-overlay" onClick={handleJustCloseModal}>
    <div className="worlddb-mobile-modal-container" onClick={(e) => e.stopPropagation()}>
      {mobileModalMode === 'select' ? (
        <>
          <div className="worlddb-mobile-modal-header">
            <h3>Select Contacts</h3>
            <button className="worlddb-mobile-modal-close" onClick={handleJustCloseModal}>×</button>
          </div>
          <div className="worlddb-mobile-modal-content">
            <p>Select the first N entries from current filtered results:</p>
            <div className="worlddb-mobile-select-options">
              <button onClick={() => handleMobileSelect(10)}><span className="number">10</span><span className="label">Contacts</span></button>
              <button onClick={() => handleMobileSelect(25)}><span className="number">25</span><span className="label">Contacts</span></button>
              <button onClick={() => handleMobileSelect(50)}><span className="number">50</span><span className="label">Contacts</span></button>
              <button onClick={() => handleMobileSelect(100)}><span className="number">100</span><span className="label">Contacts</span></button>
            </div>
            <div className="worlddb-mobile-select-custom">
              <h4>Custom Amount:</h4>
              <div className="worlddb-mobile-select-input-group">
                <input
                  ref={customInputRef}
                  type="number"
                  placeholder="Enter number"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setTimeout(() => { if (customInputRef.current) customInputRef.current.focus(); }, 0);
                  }}
                  min="1"
                  max={displayedCount}
                  className="worlddb-mobile-select-input"
                  autoFocus={false}
                />
                <button onClick={handleCustomSelect} disabled={!customAmount || parseInt(customAmount) <= 0} className="worlddb-mobile-select-submit">Select</button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="worlddb-mobile-modal-header">
            <h3>Choose Action</h3>
            <p>{selectedCount} contacts selected</p>
            <button className="worlddb-mobile-modal-close" onClick={handleJustCloseModal}>×</button>
          </div>
          <div className="worlddb-mobile-action-grid">
            {hasWarmupFlows && (
              <button className="worlddb-mobile-action-btn flow-action" onClick={() => handleActionAndClose(handleBulkStartFlow)}>
                <div className="action-icon"><i className="fa fa-fire" style={{ fontSize: '20px', color: '#f97316' }}></i></div>
                <span className="action-label">Warmup Flow</span>
                <span className="action-count">({selectedCount})</span>
              </button>
            )}
            <button className="worlddb-mobile-action-btn cleaner-action" onClick={() => handleActionAndClose(handleOpenAiCleaner)}>
              <div className="action-icon"><img src="/img/night.svg" alt="Cleaner" /></div>
              <span className="action-label">AI Cleaner</span>
              <span className="action-count">({selectedCount})</span>
            </button>
            <button className="worlddb-mobile-action-btn spy-action" onClick={() => handleActionAndClose(handleStartMassSpy)}>
              <div className="action-icon"><i className="fa fa-search" style={{ fontSize: '18px', color: '#fff' }} /></div>
              <span className="action-label">Research</span>
              <span className="action-count">({selectedCount})</span>
            </button>
            <button className="worlddb-mobile-action-btn manager-action" onClick={() => handleActionAndClose(handleOpenAiManager)}>
              <div className="action-icon"><img src="/img/email.svg" alt="Multi" /></div>
              <span className="action-label">AI Manager</span>
              <span className="action-count">({selectedCount})</span>
            </button>
            {showAutoSendButton && (
              <button className="worlddb-mobile-action-btn autosend-action" onClick={() => handleActionAndClose(handleOpenAutoSend)}>
                <div className="action-icon"><img src="/img/paperplane.svg" alt="AutoSend" /></div>
                <span className="action-label">AI Send+</span>
                <span className="action-count">({selectedCount})</span>
              </button>
            )}
            {hasOutreachJobs && (
              <button className="worlddb-mobile-action-btn outreach-action" onClick={() => handleActionAndClose(handleOpenOutreachJobModal)}>
                <div className="action-icon"><i className="fa fa-paper-plane" style={{ fontSize: '18px', color: '#fff' }}></i></div>
                <span className="action-label">Outreach Job</span>
                <span className="action-count">({selectedCount})</span>
              </button>
            )}
          </div>
          <div className="worlddb-mobile-action-footer">
            <button className="worlddb-mobile-action-cancel" onClick={handleJustCloseModal}>Close</button>
          </div>
        </>
      )}
    </div>
  </div>
));
MobileModal.displayName = 'MobileModal';

const WorldDbTableNav = ({ 
  statusFilterMode, 
  onStatusFilterChange, 
  importFilterMode,
  onImportFilterChange,
  importOptions,
  flowFilterMode,
  onFlowFilterChange,
  flowStepFilterMode,
  onFlowStepFilterChange,
  warmupFlows = [],
  setShowUploader,
  setShowCreator,
  displayedCount,
  filteredCount,
  contactedToday,
  isSelectMode,
  toggleSelectMode,
  selectedCount,
  handleDeleteSelected,
  isDeleting = false,
  totalDatabaseCount,
  handleOpenAiManager,
  handleOpenAutoSend,
  handleStartMassSpy,
  handleOpenAiCleaner,
  handleBulkStartFlow,
  handleBulkAssignTemplate,
  hasEmailTemplates = false,
  hasWarmupFlows = false,
  hasOutreachJobs = false,
  handleOpenOutreachJobModal,
  onSearch,
  searchTerm, // Prop für kontrollierte Komponente
  isSearching, // Loading-Indikator während der Suche
  needsMigration, // Ob Migration für search_terms nötig ist
  onMigrate, // Migration-Handler
  isMigrating, // Ob gerade migriert wird
  migrationProgress, // Fortschritt 0-100
  onSelectFirst, // Prop für das Auswählen der ersten N Einträge
  isDataLoaded,
  showAutoSendButton = true, // Steuert ob der AI Send+ Button angezeigt wird
  userId, // Für Duplicate-Alerts-Badge inline im Header
  onOpenDuplicateAlert // Callback für den Panel-Klick: öffnet DuplicateActionModal in NewWorldDbTable
}) => {
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [mobileModalMode, setMobileModalMode] = useState('select'); // 'select' or 'action'
  const [customAmount, setCustomAmount] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSelectionActive, setMobileSelectionActive] = useState(false);
  const [isMobileNavCollapsed, setIsMobileNavCollapsed] = useState(true);
  const [modalAnimated, setModalAnimated] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const customInputRef = useRef(null);
  const addMenuRef = useRef(null);
  const addButtonRef = useRef(null);
  const navigate = useNavigate();

  // Close the add-menu when clicking outside.
  // The menu is rendered next to the "+"-button only and must not steal focus
  // from other popups (search, filters) — therefore we only attach the
  // listener while the menu is actually open.
  useEffect(() => {
    if (!showAddMenu) return;
    const handleClickOutside = (event) => {
      if (
        addMenuRef.current &&
        !addMenuRef.current.contains(event.target) &&
        addButtonRef.current &&
        !addButtonRef.current.contains(event.target)
      ) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddMenu]);

  // Check if device is mobile (debounced to avoid excessive re-renders)
  useEffect(() => {
    let timeoutId;
    const checkMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 768);
      }, 150);
    };
    
    setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkMobile);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Reset mobile selection wenn nicht mehr im Select Mode
  useEffect(() => {
    if (!isSelectMode) {
      setMobileSelectionActive(false);
    }
  }, [isSelectMode]);

  // Animation für Header starten
  useEffect(() => {
    if (isDataLoaded) {
      // Kurzer Timeout, damit das Element erst gerendert wird, dann die Animation startet
      const timer = setTimeout(() => {
        setShowAnimation(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShowAnimation(false);
    }
  }, [isDataLoaded]);

  // Auto-expand navigation wenn Select Mode aktiviert wird auf Mobile
  useEffect(() => {
    if (isMobile && (isSelectMode || mobileSelectionActive)) {
      setIsMobileNavCollapsed(false);
    }
  }, [isMobile, isSelectMode, mobileSelectionActive]);

  const handleMobileSelect = (amount) => {
    if (onSelectFirst) {
      onSelectFirst(amount);
      setMobileSelectionActive(true);
    }
    setCustomAmount('');
    
    // Seamless Transition zum Action Modal
    setTimeout(() => {
      if (isMobile) {
        setMobileModalMode('action');
      }
    }, 200);
  };

  const handleCustomSelect = () => {
    const amount = parseInt(customAmount);
    if (amount > 0) {
      handleMobileSelect(amount);
    }
  };

  const handleMobileCancel = () => {
    setMobileSelectionActive(false);
    setShowMobileModal(false);
    setMobileModalMode('select');
    if (toggleSelectMode) {
      toggleSelectMode(); // Deselektiert automatisch alle
    }
  };

  const handleActionAndClose = (actionFunction) => {
    setShowMobileModal(false);
    setMobileModalMode('select');
    actionFunction();
  };

  const handleCloseModal = (shouldDeselect = true) => {
    setShowMobileModal(false);
    setMobileModalMode('select');
    if (shouldDeselect) {
      setMobileSelectionActive(false);
      if (toggleSelectMode) {
        toggleSelectMode(); // Das deselektiert automatisch alle
      }
    }
  };

  const handleJustCloseModal = () => {
    setShowMobileModal(false);
    setMobileModalMode('select');
    setMobileSelectionActive(false);
    if (toggleSelectMode) {
      toggleSelectMode(); // Deselektiert alle beim Schließen
    }
  };

    

  // Zeige Header erst wenn Daten geladen sind
  if (!isDataLoaded) {
    return null;
  }

  return (
    <div className={`world-db-header-container ${isMobile ? 'mobile-layout' : ''} ${showAnimation ? 'header-fade-in' : ''}`}>
      {/* Mobile Header Row - immer sichtbar */}
      {isMobile && (
        <div className="world-db-mobile-header">
          {/* Title - versteckt wenn im Select Mode */}
          <h2 className={`world-db-title-mobile ${(isSelectMode || mobileSelectionActive) ? 'hidden' : ''}`}>
            {filteredCount !== null
              ? `${displayedCount}/${filteredCount}`
              : `${displayedCount}/${totalDatabaseCount || 0}`
            }
            <span className="contacted-today-mobile">({contactedToday} today)</span>
            {userId && (
              <span className="world-db-notice-badges">
                <DuplicateAlertsBadge userId={userId} onOpenAlert={onOpenDuplicateAlert} />
              </span>
            )}
          </h2>
          
          {/* Mobile Toggle Button */}
          <button 
            className="world-db-mobile-toggle"
            onClick={() => setIsMobileNavCollapsed(!isMobileNavCollapsed)}
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none"
              className={isMobileNavCollapsed ? '' : 'rotated'}
            >
              <path 
                d="M6 9l6 6 6-6" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Desktop Title */}
      {!isMobile && (
        <h2 className={`world-db-title ${(isSelectMode || mobileSelectionActive) ? 'hidden' : ''}`}>
          {filteredCount !== null
            ? `${displayedCount} of ${filteredCount} contacts`
            : `${displayedCount} of ${totalDatabaseCount || 0} contacts loaded`
          }
          <span className="contacted-today">({contactedToday} Contacted Today)</span>
          {userId && (
            <span className="world-db-notice-badges">
              <DuplicateAlertsBadge userId={userId} onOpenAlert={onOpenDuplicateAlert} />
            </span>
          )}
        </h2>
      )}
      
      <div className={`world-db-controls ${isMobile ? (isMobileNavCollapsed ? 'mobile-collapsed' : 'mobile-expanded') : ''} ${selectedCount > 0 && !isMobile ? 'selection-active' : ''}`}>
        {/* Mobile Select Button - nur auf Mobile und nicht im Select Mode */}
        {isMobile && !isSelectMode && !mobileSelectionActive && (
          <button 
            className="world-db-mobile-select-btn"
            onClick={() => {
              setShowMobileModal(true);
              setMobileModalMode('select');
            }}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none"
            >
              <path 
                d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            Select
          </button>
        )}

        {/* Mobile Cancel Button - nur auf Mobile und wenn Selection aktiv */}
        {isMobile && (isSelectMode || mobileSelectionActive) && (
          <button 
            className="world-db-mobile-cancel-btn"
            onClick={handleMobileCancel}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none"
            >
              <path 
                d="M18 6L6 18M6 6l12 12" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            Cancel ({selectedCount})
          </button>
        )}

        {/* Standard Select Button - nur Desktop */}
        {!isMobile && (
          <button 
            className={`world-db-select-btn ${isSelectMode ? 'active' : ''}`}
            onClick={toggleSelectMode}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none"
            >
              <path 
                d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            {isSelectMode && <>Cancel <span className="btn-count-badge">{selectedCount}</span></>}
          </button>
        )}

        {/* Action Buttons - zeige nur wenn Einträge ausgewählt sind und NICHT Mobile mit Modal */}
        {(isSelectMode || mobileSelectionActive) && selectedCount > 0 && !isMobile && (
          <>
            {/* AI Cleaner Button */}
            <button 
              className="world-db-ai-cleaner-btn world-db-action-card"
              onClick={handleOpenAiCleaner}
            >
              <span className="action-card-icon cleaner-icon">
                <img src="/img/tag_eye.svg" alt="Cleaner" />
              </span>
              <span className="action-card-text">
                <span className="action-card-title">Cleaner</span>
                <span className="action-card-subtitle">Clean Contact List</span>
              </span>
              <span className="action-card-badge">{selectedCount}</span>
            </button>

            {/* Research Button */}
            <button 
              className="world-db-mass-spy-btn world-db-action-card"
              onClick={handleStartMassSpy}
            >
              <span className="action-card-icon research-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" fill="currentColor" opacity="0.3"/><path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
              <span className="action-card-text">
                <span className="action-card-title">Research</span>
                <span className="action-card-subtitle">Find more Data</span>
              </span>
              <span className="action-card-badge">{selectedCount}</span>
            </button>

            {/* AI Manager / Multi Sender Button */}
            <button 
              className="world-db-ai-manager-btn world-db-action-card"
              onClick={handleOpenAiManager}
            >
              <span className="action-card-icon manager-icon">
                <img src="/img/sendera.svg" alt="Multi" />
              </span>
              <span className="action-card-text">
                <span className="action-card-title">Multi Sender</span>
                <span className="action-card-subtitle">Send mails faster</span>
              </span>
              <span className="action-card-badge">{selectedCount}</span>
            </button>

            {/* AutoSend Button */}
            {showAutoSendButton && (
              <button 
                className="world-db-auto-send-btn world-db-action-card"
                onClick={handleOpenAutoSend}
                title="AutoSend with optional cleaning"
              >
                <span className="action-card-icon autosend-icon">
                  <img src="/img/paperplane.svg" alt="AutoSend" />
                </span>
                <span className="action-card-text">
                  <span className="action-card-title">AI Send+</span>
                  <span className="action-card-subtitle">Auto clean & send</span>
                </span>
                <span className="action-card-badge">{selectedCount}</span>
              </button>
            )}

            {/* Bulk Flow Button */}
            {hasWarmupFlows && (
              <button 
                className="world-db-bulk-flow-btn world-db-action-card"
                onClick={handleBulkStartFlow}
                title="Start Warmup Flow for selected contacts"
              >
                <span className="action-card-icon flow-icon">
                  <img src="/img/network_1.svg" alt="Flow" />
                </span>
                <span className="action-card-text">
                  <span className="action-card-title">Start Flow</span>
                  <span className="action-card-subtitle">Start Warmup</span>
                </span>
                <span className="action-card-badge">{selectedCount}</span>
              </button>
            )}

            {/* Bulk Template Button */}
            {hasEmailTemplates && (
              <button 
                className="world-db-bulk-template-btn world-db-action-card"
                onClick={handleBulkAssignTemplate}
                title="Assign template to selected contacts"
              >
                <span className="action-card-icon template-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                </span>
                <span className="action-card-text">
                  <span className="action-card-title">Add Template</span>
                  <span className="action-card-subtitle">Assign to contacts</span>
                </span>
                <span className="action-card-badge">{selectedCount}</span>
              </button>
            )}

            {/* Outreach Job Button */}
            {hasOutreachJobs && (
              <button
                className="world-db-outreach-btn world-db-action-card"
                onClick={handleOpenOutreachJobModal}
                title="Add to Outreach Job"
              >
                <span className="action-card-icon outreach-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
                </span>
                <span className="action-card-text">
                  <span className="action-card-title">Outreach Job</span>
                  <span className="action-card-subtitle">Add to auto job</span>
                </span>
                <span className="action-card-badge">{selectedCount}</span>
              </button>
            )}

            {/* Delete Button - ganz rechts */}
            <button 
              className={`world-db-delete-btn ${isDeleting ? 'deleting' : ''}`}
              onClick={handleDeleteSelected}
              disabled={isDeleting}
              style={{ marginLeft: 'auto' }}
            >
              {isDeleting ? (
                <span className="delete-spinner"></span>
              ) : (
                <img src="/img/delete.svg" style={{width: '16px', height: '16px', filter: 'invert(21%) sepia(100%) saturate(7414%) hue-rotate(359deg) brightness(94%) contrast(118%)'}} alt="Delete" />
              )}
              {isDeleting ? 'Deleting...' : <span className="btn-count-badge delete-badge">{selectedCount}</span>}
            </button>
          </>
        )}

        {/* Filter und Search - hide when contacts selected (desktop) or mobile modal open */}
        {!(isMobile && showMobileModal) && !(selectedCount > 0 && !isMobile) && (
          <>
            {/* Search - nur auf Desktop */}
            {!isMobile && (
              <NewWorldDbSearch 
                onSearch={onSearch} 
                value={searchTerm} 
                isSearching={isSearching}
                placeholder="Search contacts..."
                needsMigration={needsMigration}
                onMigrate={onMigrate}
                isMigrating={isMigrating}
                migrationProgress={migrationProgress}
              />
            )}
            
            {/* Neuer Import-Filter */}
            <WorldDbImportFilter 
              currentImportFilter={importFilterMode}
              onImportFilterChange={onImportFilterChange}
              isLoading={false}
              importOptions={importOptions}
            />
            
            {/* Status-Filter */}
            <NewWorldDbStatusFilter 
              currentStatusFilter={statusFilterMode}
              onFilterChange={onStatusFilterChange}
              isLoading={false}
            />

            {/* Flow-Filter - nur wenn Warmup Flows vorhanden */}
            {warmupFlows.length > 0 && (
              <WorldDbFlowFilter
                warmupFlows={warmupFlows}
                currentFlowFilter={flowFilterMode}
                onFilterChange={onFlowFilterChange}
                currentStepFilter={flowStepFilterMode}
                onStepFilterChange={onFlowStepFilterChange}
                isLoading={false}
              />
            )}
            
            {/* Add Menu Trigger ("+") — import, download, add contact, API. */}
            {!isMobile && (
              <div className="world-db-add-wrapper">
                <button
                  ref={addButtonRef}
                  className={`world-db-add-btn ${showAddMenu ? 'open' : ''}`}
                  onClick={() => setShowAddMenu(prev => !prev)}
                  title="Add contacts"
                  aria-haspopup="menu"
                  aria-expanded={showAddMenu}
                  type="button"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                </button>

                {showAddMenu && (
                  <div className="world-db-add-menu" ref={addMenuRef} role="menu">
                    <button
                      type="button"
                      className="world-db-add-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setShowAddMenu(false);
                        if (setShowUploader) setShowUploader(true);
                      }}
                    >
                      <span className="world-db-add-menu-icon import">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className="world-db-add-menu-text">
                        <span className="world-db-add-menu-title">Import List</span>
                        <span className="world-db-add-menu-subtitle">Upload CSV or TXT file</span>
                      </span>
                    </button>

                    <button
                      type="button"
                      className="world-db-add-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setShowAddMenu(false);
                        setShowDownloadModal(true);
                      }}
                    >
                      <span className="world-db-add-menu-icon download">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className="world-db-add-menu-text">
                        <span className="world-db-add-menu-title">Download List</span>
                        <span className="world-db-add-menu-subtitle">Export CSV with all fields</span>
                      </span>
                    </button>

                    <button
                      type="button"
                      className="world-db-add-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setShowAddMenu(false);
                        if (setShowCreator) setShowCreator(true);
                      }}
                    >
                      <span className="world-db-add-menu-icon create">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                          <path d="M19 8v6M22 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className="world-db-add-menu-text">
                        <span className="world-db-add-menu-title">Add Contact</span>
                        <span className="world-db-add-menu-subtitle">Create one entry manually</span>
                      </span>
                    </button>

                    {/* API option — Settings → API (keys, docs, field schema). */}
                    <button
                      type="button"
                      className="world-db-add-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setShowAddMenu(false);
                        navigate('/world?view=settings&tab=api');
                      }}
                    >
                      <span className="world-db-add-menu-icon api">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M9 9l-3 3 3 3M15 9l3 3-3 3M14 4l-4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span className="world-db-add-menu-text">
                        <span className="world-db-add-menu-title">
                          Connect via API
                          <span className="world-db-add-menu-tag">Dev</span>
                        </span>
                        <span className="world-db-add-menu-subtitle">Stream contacts from your tools</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {showDownloadModal && (
        <WorldDbDownloadModal
          userId={userId}
          importOptions={importOptions}
          initialListName={importFilterMode}
          onClose={() => setShowDownloadModal(false)}
        />
      )}

      {isMobile && showMobileModal && (
        <MobileModal
          mobileModalMode={mobileModalMode}
          handleJustCloseModal={handleJustCloseModal}
          handleMobileSelect={handleMobileSelect}
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          customInputRef={customInputRef}
          displayedCount={displayedCount}
          handleCustomSelect={handleCustomSelect}
          selectedCount={selectedCount}
          handleActionAndClose={handleActionAndClose}
          hasWarmupFlows={hasWarmupFlows}
          handleBulkStartFlow={handleBulkStartFlow}
          handleOpenAiCleaner={handleOpenAiCleaner}
          handleStartMassSpy={handleStartMassSpy}
          handleOpenAiManager={handleOpenAiManager}
          showAutoSendButton={showAutoSendButton}
          handleOpenAutoSend={handleOpenAutoSend}
          hasOutreachJobs={hasOutreachJobs}
          handleOpenOutreachJobModal={handleOpenOutreachJobModal}
        />
      )}
    </div>
  );
};

export default WorldDbTableNav; 